import React, { useState } from 'react';
import CoreIssueDetailsForm from './form/CoreIssueDetailsForm';
import DocumentsLinksForm from './form/DocumentsLinksForm';
import StakeholdersForm from './form/StakeholdersForm';
import PoliticalStrategicForm from './form/PoliticalStrategicForm';
import CampaignOutcomeForm from './form/CampaignOutcomeForm';
import CampaignFormHeader from './form/CampaignFormHeader';
import CampaignFormTabs from './form/CampaignFormTabs';
import CampaignFormActions from './form/CampaignFormActions';
import ExportDataDialog from './ExportDataDialog';
import { CampaignFormData, UploadedDocument } from '../types';
import { getDemoData } from '../utils/campaignDemoData';
import { validateFormDataForSerialization, cleanFormDataForSerialization } from '../utils/formDataValidator';
import { toast } from 'react-hot-toast';

interface CampaignFormProps {
  onSubmit: (data: CampaignFormData) => void;
}

const CampaignForm: React.FC<CampaignFormProps> = ({ onSubmit }) => {
  const [formData, setFormData] = useState<CampaignFormData>({
    core_issue_details: {
      campaign_name: '',
      campaign_type: 'Industrial Outcome',
      political_issue: '',
      issue_description: '',
      target_entity: '',
      campaign_timeline: '',
      current_status: 'Planning',
      start_date: '',
    },
    documents_and_links: {
      news_links: [],
      reference_links: [],
      uploaded_documents: [],
    },
    stakeholders: {
      government_officials: [],
      politicians: [],
      bureaucrats: [],
      political_allies: [],
      opposition_groups: [],
      voters_constituents: [],
      media_outlets: [],
      advocacy_groups: [],
      political_parties: [],
      union_leadership: [],
      community_context: '',
    },
    political_and_strategic: {
      government_motivations: '',
      opposition_tactics: '',
      political_vulnerabilities: '',
      coalition_opportunities: '',
    },
    campaign_outcome: {
      winning_aspiration: '',
      success_metrics: '',
      target_audience: '',
    },
  });
  
  const [activeSection, setActiveSection] = useState('core_issue_details');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submissionTimeout, setSubmissionTimeout] = useState<NodeJS.Timeout | null>(null);

  const sections = [
    { id: 'core_issue_details', label: 'Core Campaign Details' },
    { id: 'documents_and_links', label: 'Documents & Links' },
    { id: 'stakeholders', label: 'Political Stakeholders' },
    { id: 'political_and_strategic', label: 'Political & Strategic' },
    { id: 'campaign_outcome', label: 'Winning Aspiration' },
  ];

  const handleUseDemoData = () => {
    setFormData(getDemoData());
    setErrors({});
  };

  const handleExportCurrentData = () => {
    // Check if there's meaningful data to export
    const hasData = formData.core_issue_details.campaign_name || 
                   formData.core_issue_details.political_issue ||
                   formData.campaign_outcome.winning_aspiration;
    
    if (!hasData) {
      toast.error('No campaign data to export. Please fill in some form fields first.');
      return;
    }
    
    setShowExportDialog(true);
  };

  const handleInputChange = (section: keyof CampaignFormData, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...(prev[section] as object),
        [field]: value,
      },
    }));
    
    if (errors[`${section}.${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`${section}.${field}`]: '',
      }));
    }
  };

  const handleArrayInputChange = (section: keyof CampaignFormData, field: string, value: string[]) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...(prev[section] as object),
        [field]: value,
      },
    }));
  };

  const handleDocumentUpload = (document: UploadedDocument) => {
    // Validate the document object before adding it
    if (!document.id || typeof document.id !== 'string' ||
        !document.name || typeof document.name !== 'string' ||
        typeof document.content !== 'string') {
      console.error('Invalid document object:', document);
      toast.error('Error: Invalid document format');
      return;
    }

    // Create a clean document object with only serializable properties
    const cleanDocument: UploadedDocument = {
      id: document.id,
      name: document.name,
      content: document.content,
      type: document.type || 'other',
    };

    setFormData(prev => ({
      ...prev,
      documents_and_links: {
        ...prev.documents_and_links,
        uploaded_documents: [...prev.documents_and_links.uploaded_documents, cleanDocument],
      },
    }));
  };

  const handleDocumentRemove = (documentId: string) => {
    setFormData(prev => ({
      ...prev,
      documents_and_links: {
        ...prev.documents_and_links,
        uploaded_documents: prev.documents_and_links.uploaded_documents.filter(
          doc => doc.id !== documentId
        ),
      },
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.core_issue_details.campaign_name) {
      newErrors['core_issue_details.campaign_name'] = 'Campaign name is required';
    }
    
    if (!formData.core_issue_details.target_entity) {
      newErrors['core_issue_details.target_entity'] = 'Target entity is required';
    }
    
    if (!formData.core_issue_details.political_issue) {
      newErrors['core_issue_details.political_issue'] = 'Political issue is required';
    }
    
    if (!formData.core_issue_details.issue_description) {
      newErrors['core_issue_details.issue_description'] = 'Issue description is required';
    }
    
    if (!formData.campaign_outcome.winning_aspiration) {
      newErrors['campaign_outcome.winning_aspiration'] = 'Winning aspiration is required';
    }
    
    if (!formData.campaign_outcome.success_metrics) {
      newErrors['campaign_outcome.success_metrics'] = 'Success metrics are required';
    }
    
    if (!formData.campaign_outcome.target_audience) {
      newErrors['campaign_outcome.target_audience'] = 'Target audience is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    if (validateForm()) {
      setIsSubmitting(true);
      setSubmitError(null);
      console.log('🚀 Starting form submission process...');
      
      // Set a timeout to prevent getting stuck in submitting state
      const timeout = setTimeout(() => {
        console.error('⏰ Form submission timeout after 30 seconds');
        setIsSubmitting(false);
        setSubmitError('Submission timed out. Please try again or refresh the page.');
        toast.error('Submission timed out. Please try again.');
      }, 30000);
      
      setSubmissionTimeout(timeout);
      
      try {
        // Validate form data for serialization before submitting
        console.log('🔍 Validating form data for serialization...');
        const validation = validateFormDataForSerialization(formData);
        if (!validation.isValid) {
          console.error('❌ Form validation failed:', validation.errors);
          toast.error('Form contains invalid data. Please check your uploads and try again.');
          setIsSubmitting(false);
          if (timeout) clearTimeout(timeout);
          return;
        }

        // Clean the form data before submission
        console.log('🧹 Cleaning form data for submission...');
        const cleanedData = cleanFormDataForSerialization(formData);
        console.log('✅ Form data cleaned and ready for submission');
        
        // Call onSubmit and handle the response properly
        await Promise.resolve(onSubmit(cleanedData));
        
        // Clear timeout on successful submission
        if (timeout) clearTimeout(timeout);
        console.log('✅ Form submission completed successfully');
        
      } catch (error) {
        console.error('❌ Form submission error:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        setSubmitError(errorMessage);
        toast.error(`Error submitting form: ${errorMessage}`);
        setIsSubmitting(false);
        if (timeout) clearTimeout(timeout);
      }
    } else {
      const errorSections = Object.keys(errors).map(key => key.split('.')[0]);
      if (errorSections.length > 0) {
        const firstErrorSection = errorSections[0] as keyof CampaignFormData;
        setActiveSection(firstErrorSection);
      }
    }
  };

  // Cleanup timeout on component unmount
  React.useEffect(() => {
    return () => {
      if (submissionTimeout) {
        clearTimeout(submissionTimeout);
      }
    };
  }, [submissionTimeout]);

  const handlePrevious = () => {
    const currentIndex = sections.findIndex(s => s.id === activeSection);
    if (currentIndex > 0) {
      setActiveSection(sections[currentIndex - 1].id);
    }
  };

  const handleNext = () => {
    const currentIndex = sections.findIndex(s => s.id === activeSection);
    if (currentIndex < sections.length - 1) {
      setActiveSection(sections[currentIndex + 1].id);
    }
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'core_issue_details':
        return (
          <CoreIssueDetailsForm
            data={formData.core_issue_details}
            onChange={(field, value) => handleInputChange('core_issue_details', field, value)}
            errors={errors}
          />
        );
      
      case 'documents_and_links':
        return (
          <DocumentsLinksForm
            data={formData.documents_and_links}
            onArrayChange={(field, value) => handleArrayInputChange('documents_and_links', field, value)}
            onDocumentUpload={handleDocumentUpload}
            onDocumentRemove={handleDocumentRemove}
          />
        );
      
      case 'stakeholders':
        return (
          <StakeholdersForm
            data={formData.stakeholders}
            onChange={(field, value) => handleInputChange('stakeholders', field, value)}
            onArrayChange={(field, value) => handleArrayInputChange('stakeholders', field, value)}
          />
        );
      
      case 'political_and_strategic':
        return (
          <PoliticalStrategicForm
            data={formData.political_and_strategic}
            onChange={(field, value) => handleInputChange('political_and_strategic', field, value)}
          />
        );
      
      case 'campaign_outcome':
        return (
          <CampaignOutcomeForm
            data={formData.campaign_outcome}
            onChange={(field, value) => handleInputChange('campaign_outcome', field, value)}
            errors={errors}
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="bg-white p-8 rounded-lg shadow-lg">
      <CampaignFormHeader 
        onUseDemoData={handleUseDemoData}
        onExportCurrentData={handleExportCurrentData}
      />
      
      <CampaignFormTabs
        sections={sections}
        activeSection={activeSection}
        onSectionChange={setActiveSection}
      />
      
      {submitError && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="text-red-800">
              <strong>Submission Error:</strong> {submitError}
            </div>
            <button
              onClick={() => setSubmitError(null)}
              className="ml-auto text-red-600 hover:text-red-800"
            >
              ×
            </button>
          </div>
          <div className="mt-2 text-sm text-red-600">
            Try refreshing the page if the issue persists.
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        {renderSection()}
        
        <CampaignFormActions
          sections={sections}
          activeSection={activeSection}
          isSubmitting={isSubmitting}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onSubmit={handleSubmit}
        />
      </form>

      <ExportDataDialog
        isOpen={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        formData={formData}
      />
    </div>
  );
};

export default CampaignForm;
